/**
 * What each part of the basket earned — ROADMAP 9.10.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE QUESTION THIS EXISTS FOR
 *
 * "The trade made ₹10,200" is the headline and it hides the thing worth
 * learning: whether the thesis worked and the hedge merely cost its premium,
 * or the thesis failed and an adjustment rescued it. Those are opposite
 * lessons behind one number, and the only way to tell them apart afterwards
 * is to have labelled the legs at the time.
 *
 * GROSS, like every per-leg figure on this page. `realisedPnl` is gross by
 * 6.4's rule and charges are a trade-level line; splitting those per role
 * would need a per-leg breakdown stored at fill time, which `chargesTotal`
 * only has when the broker reported it.
 *
 * UNTAGGED IS SHOWN, NOT HIDDEN. A basket with one labelled leg would
 * otherwise print a split that silently excludes most of the money. If the
 * numbers do not add up to the trade's gross, the reason has to be on screen.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { Tags } from 'lucide-react'
import { LEG_ROLES, LEG_ROLE_LABEL, LEG_ROLE_MEANING, splitByRole } from '@apna/shared'
import { cn } from '@/lib/utils'
import { money } from '../utils/format'
import type { JournalLeg } from '../types'

export function JournalRoles({ legs }: { legs: readonly JournalLeg[] }) {
  const split = splitByRole(legs.map((l) => ({ role: l.role, value: l.realisedPnl })))

  /*
   * Nothing labelled means nothing to say. Four zeroes and an empty heading
   * would be a panel claiming an analysis it does not have.
   */
  if (split.empty) return null

  const rows = LEG_ROLES.filter((r) => split.byRole[r].legs > 0).map((r) => ({
    key: r,
    label: LEG_ROLE_LABEL[r],
    title: LEG_ROLE_MEANING[r],
    ...split.byRole[r],
  }))

  return (
    <section className="jn-panel" aria-label="Result by leg role" data-testid="journal-roles">
      <header className="jn-panel-heading">
        <div className="flex items-center gap-2">
          <Tags size={14} className="text-primary" />
          <h3>By role</h3>
        </div>
        <span className="jn-caption">Gross</span>
      </header>
      <dl className="jn-roles">
        {rows.map((r) => (
          <div key={r.key}>
            <dt title={r.title}>
              {r.label} <span className="jn-roles-count">{r.legs}</span>
            </dt>
            <dd className={cn('num', r.total >= 0 ? 'text-success' : 'text-destructive')}>
              {money(r.total)}
            </dd>
          </div>
        ))}
        {split.untagged.legs > 0 && (
          <div>
            <dt title="Legs with no role recorded. Counted here rather than folded into the thesis.">
              Untagged <span className="jn-roles-count">{split.untagged.legs}</span>
            </dt>
            <dd className="num text-muted-foreground">{money(split.untagged.total)}</dd>
          </div>
        )}
      </dl>
    </section>
  )
}
